import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";

import { itemRouteBuilder, NamedRts } from "@/router";

import {
  filter as Rfilter,
  reduce as Rreduce,
  reduced as Rreduced,
} from "ramda";

import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import { isToday, format, subMonths, addMonths } from "date-fns";

import IlsctlLineItemPartial from "@/components/IlsctlLineItems/IlsctlLineItemPartial.vue";
import IlsctlLineItemHeader from "@/components/IlsctlLineItems/IlsctlLineItemHeader.vue";
import TruckLoadLineItemPartial from "@/components/TruckLoadLineItems/TruckLoadLineItemPartial.vue";
import ListPageHeader from "@/components/ListPageHeader/ListPageHeader.vue";
import { debounce } from "@/utilities/debounce";

// import { trucksController } from "@/controllers/trucks";
// import { sellContractsController } from "@/controllers/sellContracts";
// import { inventoryLotsController } from "@/controllers/inventoryLots";

import { truckLoadsController } from "@/controllers/truckLoads";
import { TruckLoad } from "@/models/TruckLoad";
import { IlsctlLineItem } from "@/models/IlsctlLineItem";
import { formatTotal } from "@/utilities/display-helpers";

import { genericSort, sortIcon } from "@/components/helpers";

import { JSConn } from "@/utilities/js-conn";

import {
  arrowUpHandler,
  arrowDownHandler,
  filterOnInputHandler,
  filterItems,
} from "@/utilities/pickListHelpers";

export default defineComponent({
  name: "truckLoads",
  components: {
    LoadingAnimation,
    IlsctlLineItemPartial,
    IlsctlLineItemHeader,
    TruckLoadLineItemPartial,
    ListPageHeader,
  },

  setup(_props, ctx) {
    const $store = ctx.root.$store;
    const dataLoaded = ref(false);
    const loadingMessage =
      "Please wait while the truck loads are pulled in from the api...";

    const filter = ref("");

    const clearFilter = () => {
      filterOnInputHandler(
        apiTruckLoads,
        truckLoads,

        "",
        filter,
        debouncedFilteredTruckLoads,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const firstInput = ref<null | HTMLInputElement>(null);
    const showMenu = ref(false);

    const toggleMenu = (newState: boolean) => {
      showMenu.value = newState;
      if (!newState) {
        ctx.root.$nextTick(() => {
          if (firstInput.value) {
            firstInput.value.focus();
          }
        });
      }
    };

    const filterableKeys: Array<keyof TruckLoad> = [
      "name",
      "status",
      "completed_date",
    ];

    const apiTruckLoads = computed(() => {
      return $store.getters.truckLoads as TruckLoad[];
    });

    const truckLoads = ref<TruckLoad[]>([]);

    const sortKey = ref<keyof TruckLoad>("name");
    const sortAsc = ref(true);

    const localSortIcon = (lSortKey: "name" | "completed_date") => {
      return sortIcon(sortAsc, sortKey, lSortKey);
    };

    const updateSort = (lSortKey: keyof TruckLoad) => {
      truckLoads.value = genericSort(
        [...truckLoads.value],
        sortAsc,
        sortKey,
        lSortKey,
      );
    };

    // const truckLoads = computed(() => {
    //   let tmpResults = [...localTruckLoads.value];

    //   if (filter.value !== "") {
    //     const tmpReg = new RegExp(filter.value, "i");

    //     tmpResults = Rfilter((item: TruckLoad) => {
    //       const nameResult = tmpReg.test(item.name);
    //       const bolResult = tmpReg.test(item.bill_of_lading);

    //       let ilsctlLineItemResult = Rreduce(
    //         (acc: boolean, innerItem: IlsctlLineItem) => {
    //           if (acc) {
    //             return Rreduced(acc);
    //           } else {
    //             const ilResult = tmpReg.test(innerItem.inventory_lot_label);
    //             const scResult = tmpReg.test(innerItem.sell_contract_label);
    //             const unitsResult = tmpReg.test(innerItem.lot_unit_label);
    //             return ilResult || scResult || unitsResult;
    //           }
    //         },
    //         false,
    //         item.ilsctl_line_items,
    //       ) as boolean;

    //       return nameResult || bolResult || ilsctlLineItemResult;
    //     }, tmpResults);
    //   }

    //   return tmpResults;
    // });
    const filterTruckLoads = (filter: string, tl: TruckLoad[]) => {
      return filterItems(filter, filterableKeys, tl);
    };

    const debouncedFilteredTruckLoads = debounce(filterTruckLoads, 500);

    const editRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.buildTruckLoad, id);
    };

    const MONTHS_PER_PAGE = 1;
    const toDate = ref<Date>(new Date());
    const fromDate = ref<Date>(subMonths(toDate.value, MONTHS_PER_PAGE));

    const formattedFromDate = computed(() => {
      return format(fromDate.value, "yyyy-MM-dd");
    });

    const formattedToDate = computed(() => {
      return format(toDate.value, "yyyy-MM-dd");
    });

    const loadData = (newFromDate: Date, newToDate: Date) => {
      // const jsConnTrucks = new JSConn();
      const jsConnTruckLoads = new JSConn();
      // const jsConnInventoryLots = new JSConn();
      // const jsConnSellContracts = new JSConn();
      // const jsConnLotUnits = new JSConn();
      if (isToday(newToDate)) {
        jsConnTruckLoads.formParams = {
          from_date: format(newFromDate, "yyyy-MM-dd"),
        };
      } else {
        jsConnTruckLoads.formParams = {
          to_date: format(newToDate, "yyyy-MM-dd"),
          from_date: format(newFromDate, "yyyy-MM-dd"),
        };
      }

      return Promise.all([
        // trucksController.needTrucks(jsConnTrucks),
        truckLoadsController.needTruckLoads(jsConnTruckLoads),
        // inventoryLotsController.needInventoryLots(jsConnInventoryLots),
        // sellContractsController.needSellContracts(jsConnSellContracts),
        // $store.dispatch("needLotUnits", jsConnLotUnits),
      ]).then(() => {
        truckLoads.value = [...apiTruckLoads.value];
        // flip the sort, otherwise on load it would be off
        sortAsc.value = !sortAsc.value;
        updateSort(sortKey.value);

        // set the dates to the correct value
        fromDate.value = newFromDate;
        toDate.value = newToDate;
        dataLoaded.value = true;
        return Promise.resolve();
      });
    };

    onMounted(() => {
      loadData(fromDate.value, toDate.value).finally(() => {
        dataLoaded.value = true;
      });
    });

    // this is for when the truck loads are used as a picker
    // they make the arrow keys work correctly
    const selectedIndex = ref(-1);

    const onArrowDown = (evt: any) => {
      evt.preventDefault();
      arrowDownHandler(selectedIndex, truckLoads.value.length);
    };

    const onArrowUp = (evt: any) => {
      evt.preventDefault();
      arrowUpHandler(selectedIndex);
    };

    const changeToDate = ({ target }: { target: HTMLInputElement }) => {
      if (target.valueAsDate) {
        toDate.value = target.valueAsDate;
      } else {
        toDate.value = new Date();
      }
    };

    const changeFromDate = ({ target }: { target: HTMLInputElement }) => {
      if (target.valueAsDate) {
        fromDate.value = target.valueAsDate;
      } else {
        fromDate.value = new Date();
      }
    };

    const dateRangeSubmit = (evt: any) => {
      evt.preventDefault();
      dataLoaded.value = false;
      loadData(fromDate.value, toDate.value).finally(() => {
        dataLoaded.value = true;
      });
    };

    const filterOnInput = ({ target }: { target: HTMLInputElement }) => {
      filterOnInputHandler(
        apiTruckLoads,
        truckLoads,

        target.value,
        filter,
        debouncedFilteredTruckLoads,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterOnEnter = (evt: any) => {
      evt.preventDefault();
      let tmpResult = truckLoads.value[selectedIndex.value];

      if (!tmpResult) {
        tmpResult = truckLoads.value[0];
      }

      ctx.root.$router.push(editRouteBuilder(tmpResult.id));
    };

    const getPreviousTruckLoads = () => {
      dataLoaded.value = false;
      loadData(
        subMonths(fromDate.value, MONTHS_PER_PAGE),
        subMonths(toDate.value, MONTHS_PER_PAGE),
      ).finally(() => {
        dataLoaded.value = true;
      });
    };

    const getNextTruckLoads = () => {
      dataLoaded.value = false;
      loadData(
        addMonths(fromDate.value, MONTHS_PER_PAGE),
        addMonths(toDate.value, MONTHS_PER_PAGE),
      ).finally(() => {
        dataLoaded.value = true;
      });
    };

    return {
      dataLoaded,
      loadingMessage,

      formatTotal,

      truckLoads,

      editRouteBuilder,

      filter,
      filterOnEnter,
      filterOnInput,
      clearFilter,

      updateSort,
      localSortIcon,

      showMenu,
      toggleMenu,
      firstInput,

      onArrowDown,
      onArrowUp,
      selectedIndex,
      // sendWorkOrder,

      getPreviousTruckLoads,
      getNextTruckLoads,
      formattedFromDate,
      formattedToDate,
      changeFromDate,
      changeToDate,
      dateRangeSubmit,
    };
  },
});
